import Phaser from 'phaser';
import type { SaveManager } from '@/game/systems/SaveManager';
import type { AudioManager } from '@/game/systems/AudioManager';
import { INTERNAL_HEIGHT, INTERNAL_WIDTH } from '@/game/config/GameConfig';
import { PALETTE, bodyTextStyle, createButton, titleTextStyle } from '@/game/ui/theme';
import { vibrate } from '@/utils/effects';

/** Large touch-friendly +/- steppers rather than drag-sliders -- reliable on touch
 * without needing custom drag-hit-testing, while still meeting every setting the
 * design calls for. */
export class SettingsScene extends Phaser.Scene {
  private saveManager!: SaveManager;
  private audioManager!: AudioManager;

  constructor() {
    super('SettingsScene');
  }

  create(): void {
    this.saveManager = this.game.registry.get('saveManager');
    this.audioManager = this.game.registry.get('audioManager');

    this.add.rectangle(0, 0, INTERNAL_WIDTH, INTERNAL_HEIGHT, PALETTE.bgBottom).setOrigin(0);
    this.add.text(INTERNAL_WIDTH / 2, 70, 'Settings', titleTextStyle(42)).setOrigin(0.5);

    let y = 190;
    const rowGap = 110;

    y = this.buildPercentRow(y, 'Music Volume', () => this.saveManager.getSettings().musicVolume, (v) => {
      this.saveManager.updateSettings({ musicVolume: v });
      this.audioManager.setMusicVolume(v);
    }) + rowGap;

    y = this.buildPercentRow(y, 'SFX Volume', () => this.saveManager.getSettings().sfxVolume, (v) => {
      this.saveManager.updateSettings({ sfxVolume: v });
      this.audioManager.setSfxVolume(v);
    }) + rowGap;

    y = this.buildToggleRow(y, 'Vibration', () => this.saveManager.getSettings().vibration, (v) => {
      this.saveManager.updateSettings({ vibration: v });
      if (v) vibrate(20);
    }) + rowGap;

    y = this.buildToggleRow(y, 'Reduced Effects', () => this.saveManager.getSettings().reducedEffects, (v) =>
      this.saveManager.updateSettings({ reducedEffects: v })
    ) + rowGap;

    y = this.buildOffsetRow(y) + rowGap;

    const resetBtn = createButton(this, INTERNAL_WIDTH / 2, y + 20, 380, 74, 'Reset Save Data', PALETTE.danger, 24);
    resetBtn.on('pointerup', () => {
      this.saveManager.resetSaveData();
      this.scene.restart();
    });

    const backBtn = createButton(this, 90, 70, 120, 60, 'Back', PALETTE.panelLight, 22);
    backBtn.on('pointerup', () => this.scene.start('HomeScene'));
  }

  private buildPercentRow(y: number, label: string, get: () => number, set: (v: number) => void): number {
    this.add.text(60, y, label, bodyTextStyle(26)).setOrigin(0, 0.5);
    const valueText = this.add.text(INTERNAL_WIDTH - 220, y, `${Math.round(get() * 100)}%`, bodyTextStyle(26, '#ffe066')).setOrigin(0.5);

    const minusBtn = createButton(this, INTERNAL_WIDTH - 150, y, 60, 60, '-', PALETTE.panelLight, 30);
    const plusBtn = createButton(this, INTERNAL_WIDTH - 70, y, 60, 60, '+', PALETTE.panelLight, 30);

    minusBtn.on('pointerup', () => {
      const v = Phaser.Math.Clamp(get() - 0.1, 0, 1);
      set(v);
      valueText.setText(`${Math.round(v * 100)}%`);
    });
    plusBtn.on('pointerup', () => {
      const v = Phaser.Math.Clamp(get() + 0.1, 0, 1);
      set(v);
      valueText.setText(`${Math.round(v * 100)}%`);
    });

    return y;
  }

  private buildToggleRow(y: number, label: string, get: () => boolean, set: (v: boolean) => void): number {
    this.add.text(60, y, label, bodyTextStyle(26)).setOrigin(0, 0.5);
    const toggleBtn = createButton(this, INTERNAL_WIDTH - 130, y, 180, 64, get() ? 'ON' : 'OFF', get() ? PALETTE.success : PALETTE.panelLight, 24);
    toggleBtn.on('pointerup', () => {
      const next = !get();
      set(next);
      const bg = toggleBtn.list[1] as Phaser.GameObjects.Graphics;
      const text = toggleBtn.list[2] as Phaser.GameObjects.Text;
      bg.clear();
      bg.fillStyle(next ? PALETTE.success : PALETTE.panelLight, 1);
      bg.fillRoundedRect(-90, -32, 180, 64, 24);
      bg.lineStyle(3, 0xffffff, 0.55);
      bg.strokeRoundedRect(-90, -32, 180, 64, 24);
      text.setText(next ? 'ON' : 'OFF');
    });
    return y;
  }

  private buildOffsetRow(y: number): number {
    this.add.text(60, y, 'Audio Offset', bodyTextStyle(26)).setOrigin(0, 0.5);
    const valueText = this.add
      .text(INTERNAL_WIDTH - 220, y, `${this.saveManager.getSettings().audioOffsetMs}ms`, bodyTextStyle(24, '#ffe066'))
      .setOrigin(0.5);

    const minusBtn = createButton(this, INTERNAL_WIDTH - 150, y, 60, 60, '-', PALETTE.panelLight, 26);
    const plusBtn = createButton(this, INTERNAL_WIDTH - 70, y, 60, 60, '+', PALETTE.panelLight, 26);

    minusBtn.on('pointerup', () => {
      const v = this.saveManager.getSettings().audioOffsetMs - 10;
      this.saveManager.updateSettings({ audioOffsetMs: v });
      valueText.setText(`${v}ms`);
    });
    plusBtn.on('pointerup', () => {
      const v = this.saveManager.getSettings().audioOffsetMs + 10;
      this.saveManager.updateSettings({ audioOffsetMs: v });
      valueText.setText(`${v}ms`);
    });

    return y;
  }
}
